<template>
  <HgCardElevated :class="$style.card">
    <HgNewConstructionBadge text="long" />
    <div :class="$style.cardContent">
      <div v-if="showFurtherObjects" :class="$style.section">
        <div>{{ t('newProjectCard.objects') }}:</div>

        <template v-if="newProjectInfo.otherListings?.length">
          <HgLink ref="ncRecommendedInteraction" @click="emitScrollToOtherListings">
            <HgIcon icon="House" size="large" theme="current" />
            {{ newProjectInfo.otherListings.length }}
          </HgLink>
        </template>
        <template v-else>
          <span>
            <HgIcon icon="House" size="large" />
            0
          </span>
        </template>
      </div>
      <div v-else :class="$style.craneIconContainer">
        <ConstructionCraneGreen v-if="isPlatformIs24()" :class="$style.greenCraneIcon" />
        <ConstructionCrane v-else :class="$style.craneIcon" />
      </div>

      <div :class="$style.section">
        <div>{{ t('newProjectCard.available') }}:</div>
        <div>
          <span>
            <HgIcon icon="Timer" size="x-large" />
            {{ availableDate }}
          </span>
        </div>
      </div>

      <div :class="$style.section">
        <div>{{ t('newProjectCard.information') }}:</div>

        <template v-if="newProjectInfo.projectUrl">
          <HgLink
            ref="ncProjectUrl"
            :href="newProjectInfo.projectUrl"
            target="_blank"
            @click="
              trackNewConstructionProjectLink(
                props.newProjectInfo.projectUrl || '',
                t('newProjectCard.website').toString(),
              )
            "
          >
            <HgIcon icon="ExternalLink" size="large" theme="current" />
            {{ t('newProjectCard.website') }}
          </HgLink>
        </template>
        <template v-else>
          <span>
            <HgIcon icon="ExternalLink" size="large" />
            -
          </span>
        </template>
      </div>
    </div>
  </HgCardElevated>
</template>
<script lang="ts" setup>
import { HgCardElevated, HgIcon, HgLink, HgNewConstructionBadge } from '@smg-real-estate/domus';
import { ProjectType } from '@smg-real-estate/hg-rets-schema';
import { computed, defineAsyncComponent } from 'vue';

import { isPlatformIs24 } from '@/helpers/platformDetection';
import { NewConstructionDataWithOtherListings } from '@/models';

import { useI18n } from 'vue-i18n';
import {
  trackNewConstructionInteraction,
  trackNewConstructionProjectLink,
} from './NewBuildingProjectCard.tracking';

const { t } = useI18n();

defineOptions({
  name: 'NewBuildingProjectCard',
});

const ConstructionCrane = defineAsyncComponent(
  () => import('@/assets/NewBuildingProjectCard/construction_crane.svg?component'),
);
const ConstructionCraneGreen = defineAsyncComponent(
  () => import('@/assets/NewBuildingProjectCard/construction_crane_green.svg?component'),
);

interface Props {
  newProjectInfo: NewConstructionDataWithOtherListings;
  availableDate: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  scrollToOtherListings: [];
}>();

const showFurtherObjects = computed((): boolean => {
  return props.newProjectInfo.projectType === ProjectType.NewConstructionPremium;
});

const emitScrollToOtherListings = (): void => {
  trackNewConstructionInteraction();
  emit('scrollToOtherListings');
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.card {
  .cardContent {
    @include body-2;

    display: flex;
    flex-wrap: wrap;
    gap: $space-50;
    justify-content: space-between;
    padding: $space-50;

    .craneIconContainer {
      display: none;
    }

    .section {
      span {
        display: flex;
        gap: $space-25;
        align-items: center;
      }
    }
  }
}

@media (min-width: $viewport-medium-min) {
  .card {
    .cardContent {
      align-items: center;
      padding: $space-100;

      .section {
        span {
          @include heading-2;
        }
      }

      .craneIconContainer {
        display: initial;

        .craneIcon {
          width: 80px;
        }

        .greenCraneIcon {
          width: 70px;
        }
      }
    }
  }
}
</style>
